type Oklch = { L: number; C: number; H: number }

const srgbToLinear = (c: number): number => {
  const x = c / 255

  return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4)
}

const linearToSrgb = (c: number): number => {
  const x = Math.max(0, Math.min(1, c))
  const v = x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055

  return Math.round(v * 255)
}

const HEX_DIGITS = /^(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

const hexToRgb = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '')
  if (!HEX_DIGITS.test(h)) return [0, 0, 0]

  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h

  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)]
}

const rgbToHex = (r: number, g: number, b: number): string => {
  const c = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')

  return `#${c(r)}${c(g)}${c(b)}`
}

export const hexToOklch = (hex: string): Oklch => {
  const [r, g, b] = hexToRgb(hex)
  const lr = srgbToLinear(r)
  const lg = srgbToLinear(g)
  const lb = srgbToLinear(b)
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb)
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb)
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb)
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s

  return { L, C: Math.hypot(A, B), H: Math.atan2(B, A) }
}

export const oklchToHex = ({ L, C, H }: Oklch): string => {
  const A = C * Math.cos(H)
  const B = C * Math.sin(H)
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3
  const r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s
  const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s
  const b = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s

  return rgbToHex(linearToSrgb(r), linearToSrgb(g), linearToSrgb(b))
}

const relLuminance = (hex: string): number => {
  const [r, g, b] = hexToRgb(hex)

  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b)
}

export const contrastRatio = (a: string, b: string): number => {
  const la = relLuminance(a)
  const lb = relLuminance(b)

  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

export const deriveSurface = (hex: string, step = 1): string => {
  const o = hexToOklch(hex)
  const darken = o.L > 0.6
  const dL = (darken ? -0.055 : 0.07) * step
  const dC = (darken ? 0.018 : -0.004) * step

  return oklchToHex({
    L: Math.max(0.08, Math.min(0.96, o.L + dL)),
    C: Math.max(0, o.C + dC),
    H: o.H
  })
}

export const deriveInk = (surfaceHex: string, baseHex: string): string => {
  const o = hexToOklch(baseHex)
  const dark = oklchToHex({ L: 0.24, C: Math.min(o.C * 0.5, 0.05), H: o.H })
  const light = oklchToHex({ L: 0.96, C: Math.min(o.C * 0.3, 0.028), H: o.H })

  return contrastRatio(surfaceHex, dark) >= contrastRatio(surfaceHex, light) ? dark : light
}

const AA_NORMAL = 4.5

export const pillInk = (background: string, primary: string): string => {
  if (contrastRatio(background, primary) >= AA_NORMAL) return primary

  return contrastRatio(background, '#1A1A1A') >= contrastRatio(background, '#FFFFFF') ? '#1A1A1A' : '#FFFFFF'
}
